import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import ScoreBar from "@/components/ScoreBar";
import { Sparkles, Clock, MapPin, ThumbsUp, ThumbsDown, Compass } from "lucide-react";
import { Button } from "@/components/ui/button";

const BUCKET_STYLES = {
  familiar: { label: "Familiar", className: "bg-primary/10 text-primary border-primary/30" },
  adjacent: { label: "Adjacent", className: "bg-foreground/5 text-foreground border-border" },
  discovery: { label: "Discovery", className: "bg-foreground/[0.03] text-muted-foreground border-border" },
};

export default function RecommendationCard({ rec, rank, onFeedback }) {
  const f = rec.food;
  const bucket = BUCKET_STYLES[rec.bucket] || BUCKET_STYLES.familiar;
  return (
    <Card
      className="p-5 sm:p-6 lg:p-8 border-border rounded-2xl shadow-none hover:-translate-y-1 hover:border-foreground/30 transition-all"
      data-testid={`rec-card-${rank}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <span className="overline">Pick #{rank}</span>
            <Badge className={`rounded-full text-[10px] border ${bucket.className}`} data-testid={`bucket-${rank}`}>
              <Compass className="w-3 h-3 mr-1" strokeWidth={1.75} /> {bucket.label}
            </Badge>
          </div>
          <h3 className="font-serif text-2xl md:text-3xl font-semibold tracking-tight leading-tight break-words">{f.name}</h3>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-xs sm:text-sm text-muted-foreground">
            <Badge variant="outline" className="rounded-full">{f.cuisine}</Badge>
            <span className="flex items-center gap-1"><MapPin className="w-3 h-3 shrink-0" strokeWidth={1.5} />{f.region}, {f.country}</span>
            <span className="flex items-center gap-1"><Clock className="w-3 h-3 shrink-0" strokeWidth={1.5} />{f.prep_time}m</span>
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="overline">Mother Drive</div>
          <div className="font-serif text-4xl font-semibold text-primary tabular-nums leading-none">{Math.round(rec.total_score)}</div>
          {rec.confidence != null && (
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1" data-testid={`confidence-${rank}`}>
              Conf {rec.confidence}%
            </div>
          )}
        </div>
      </div>

      {/* Headline reason */}
      <div className="bg-primary/[0.04] border-l-2 border-primary pl-4 py-3 pr-3 rounded-r-lg mb-5" data-testid={`rec-headline-${rank}`}>
        <div className="flex items-center gap-2 mb-1.5">
          <Sparkles className="w-3.5 h-3.5 text-primary shrink-0" strokeWidth={1.75} />
          <span className="overline text-primary">Why YUMO Recommended This</span>
        </div>
        <p className="text-sm leading-relaxed text-foreground/90">
          {rec.headline_reason}
        </p>
      </div>

      {/* Transparent sub-scores */}
      <div className="grid grid-cols-3 md:grid-cols-6 gap-3 sm:gap-4 mb-5" data-testid={`rec-scores-${rank}`}>
        <ScoreBar label="Identity" value={rec.identity_dna} accent />
        <ScoreBar label="Regional" value={rec.regional_match} accent />
        <ScoreBar label="Mood" value={rec.mood_match} />
        <ScoreBar label="Comfort" value={rec.comfort_match} />
        <ScoreBar label="Weather" value={rec.weather_match} />
        <ScoreBar label="Diet" value={rec.diet_match} />
      </div>

      {/* Detailed bullets */}
      <ul className="text-sm space-y-1 text-foreground/85 mb-5" data-testid={`rec-bullets-${rank}`}>
        {rec.reasons.map((r, i) => (
          <li key={`${r}-${i}`} className="flex gap-2"><span className="text-primary shrink-0">✓</span><span className="leading-snug">{r}</span></li>
        ))}
      </ul>

      {/* Footer */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-1.5 text-xs">
          <Badge variant="secondary" className="rounded-full">Spice {f.spice_level}/5</Badge>
          <Badge variant="secondary" className="rounded-full capitalize">{f.diet_type}</Badge>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="rounded-full" onClick={() => onFeedback(f.id, "dislike")} data-testid={`reject-${rank}`}>
            <ThumbsDown className="w-3.5 h-3.5 mr-1" strokeWidth={1.5} /> Not for me
          </Button>
          <Button size="sm" className="rounded-full" onClick={() => onFeedback(f.id, "like")} data-testid={`accept-${rank}`}>
            <ThumbsUp className="w-3.5 h-3.5 mr-1" strokeWidth={1.5} /> I'll eat this
          </Button>
        </div>
      </div>
    </Card>
  );
}
